import { useEffect, useRef } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { invtryActions } from "./redux/inventorySlice";

// Styles
import "./App.css";
import "./pages/Styles.css";

// Pages
import Home from "./pages/Home";
import Auth from "./pages/Auth";
import Inventory from "./pages/Inventory";
import Details from "./pages/Details";
import Charts from "./pages/Charts";
import SecondHandInventory from "./pages/SecondHandInventory";

// Reports
import Report from "./pages/Report";
import ReportDate from "./pages/ReportDate";
import TableReport from "./pages/TableReport";
import CityReport from "./pages/City_Report";
import ProductsReport from "./pages/Product";

// Products
import Product from "./pages/products";
import AddProduct from "./pages/products/add";
import EditProduct from "./pages/products/edit";

// Udhar & Orders
import Udhar from "./pages/udhar";
import UdharDetails from "./pages/UdharDetails";
import ManageItemOrder from "./pages/order";
import OrderDetails from "./pages/OrderDetails";

// Alt Entries
import AltEntries from "./pages/AltEntries";
import ClearedEntries from "./pages/ClearedEntries";
import AltEntriesLedger from "./pages/AltEntriesLedger";
import AltEntriesAnalytics from "./pages/AltEntriesAnalytics";
import BanksLedger from "./pages/BanksLedger";

// Utilities
import DailyPdf from "./pages/daily-pdf";
import Calculator from "./pages/calculator";
import ThankYou from "./pages/ThankYou";

/**
 * Base path for all routes in the application
 */
export const BASE_PATH = "/analytics";

/**
 * Protected Route wrapper component
 * Redirects to auth if not authenticated
 */
const PrivateRoute = ({ children, isAuth }) => {
  return isAuth ? children : <Navigate to={`${BASE_PATH}/auth`} replace />;
};

/**
 * Route configuration for cleaner route definitions
 */
const ROUTES = [
  // Core
  { path: "/", element: Home },
  { path: "/details", element: Details },

  // Inventory
  { path: "/inventory", element: Inventory },
  { path: "/inventory2", element: Inventory, props: { alt: true } },
  { path: "/inventory3", element: Inventory, props: { sunil: true } },
  { path: "/inventory4", element: Inventory, props: { threeH: true } },

  // Second Hand Inventory
  { path: "/inventory-2h", element: SecondHandInventory },

  // Inventory History
  { path: "/inventory-history", element: Charts },
  { path: "/inventory-history2", element: Charts, props: { alt: true } },
  { path: "/inventory-history3", element: Charts, props: { sunil: true } },
  { path: "/inventory-history4", element: Charts, props: { threeH: true } },

  // Reports
  { path: "/report", element: Report },
  { path: "/report-date/:date", element: ReportDate },
  { path: "/table-report", element: TableReport },
  { path: "/city-report", element: CityReport },
  { path: "/products-report", element: ProductsReport },

  // Products
  { path: "/view", element: Product },
  { path: "/add", element: AddProduct },
  { path: "/edit/:id", element: EditProduct },

  // Udhar
  { path: "/udhars", element: Udhar },
  { path: "/udhar/details/:billno", element: UdharDetails },

  // Alt Entries
  { path: "/alt-entries", element: AltEntries },
  { path: "/alt-cleared-entries", element: ClearedEntries },
  { path: "/alt-entries-ledger", element: AltEntriesLedger },
  { path: "/alt-entries-analytics", element: AltEntriesAnalytics },
  { path: "/banks", element: BanksLedger },

  // Orders
  { path: "/manage-item", element: ManageItemOrder },
  { path: "/order/details/:billno", element: OrderDetails },

  // Utilities
  { path: "/pdfs", element: DailyPdf },
  { path: "/calculator", element: Calculator },
].map((route) => ({
  ...route,
  path: `${BASE_PATH}${route.path === "/" ? "" : route.path}`,
}));

function App() {
  const isAuth = useSelector((state) => state.invtry.isAuth);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const hasNavigatedRef = useRef(false);

  // Persist current route to localStorage
  useEffect(() => {
    localStorage.setItem("route", location.pathname);
  }, [location.pathname]);

  // Restore auth from localStorage on mount
  useEffect(() => {
    if (!isAuth) {
      const token = localStorage.getItem("token") || "";
      const level = localStorage.getItem("level") || "";

      if (token && level) {
        dispatch(invtryActions.setAuth({ token, level }));
      }
    }
  }, [isAuth, dispatch]);

  // Navigate to last visited route ONLY on initial auth restoration
  useEffect(() => {
    // Only navigate once on initial mount when auth is restored
    if (!hasNavigatedRef.current && isAuth) {
      hasNavigatedRef.current = true;

      const lastPath = localStorage.getItem("route") || "/";

      // Redirect report sub-pages to main report
      const shouldRedirectToReport =
        lastPath.includes("/products-report") ||
        lastPath.includes("/table-report");

      // Only navigate if we're not already on the target path
      if (
        location.pathname !== lastPath &&
        location.pathname === `${BASE_PATH}/auth`
      ) {
        navigate(shouldRedirectToReport ? `${BASE_PATH}/report` : lastPath);
      }
    }
  }, [isAuth, navigate, location.pathname]);

  return (
    <div className="App">
      <Routes>
        {/* Public landing page at root */}
        <Route path="/" element={<ThankYou />} />

        {/* Auth Route - redirect to home if already authenticated */}
        <Route
          path={`${BASE_PATH}/auth`}
          element={!isAuth ? <Auth /> : <Navigate to={BASE_PATH} replace />}
        />

        {/* Protected Routes */}
        {ROUTES.map(({ path, element: Element, props = {} }) => (
          <Route
            key={path}
            path={path}
            element={
              <PrivateRoute isAuth={isAuth}>
                <Element {...props} />
              </PrivateRoute>
            }
          />
        ))}
      </Routes>
    </div>
  );
}

export default App;
